import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Compass, History, MapPinned, Plus, Search, X } from "lucide-react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/Select";
import { Field, SectionHeading } from "../components/Field";
import { formatAdvisorDate } from "../../../../lib/carpintel-advisor";

const PRESSURE_TRENDS = [
  { value: "not-provided", label: "Not provided" },
  { value: "rising", label: "Rising" },
  { value: "falling", label: "Falling" },
  { value: "stable", label: "Stable" },
];

function todayInput() {
  const now = new Date();
  const pad = (v: number) => String(v).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function currentTimeInput() {
  const now = new Date();
  const pad = (v: number) => String(v).padStart(2, "0");
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

// ─── Canonical lake search for Future Trip ────────────────────────────────────

export interface CanonicalLake {
  id: string;
  name: string;
  venue_name: string | null;
  county_region: string | null;
  country: string | null;
}

function CanonicalLakePicker({
  value,
  onChange,
  onAddLake,
}: {
  value: CanonicalLake | null;
  onChange: (lake: CanonicalLake | null) => void;
  /** Called when the user taps "Add a lake" — lets the parent navigate with state. */
  onAddLake: (searchQuery: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const searchQuery = useQuery({
    queryKey: ["canonical_lakes_search", query],
    queryFn: () =>
      fetch(`/app-api/canonical-lakes/search?q=${encodeURIComponent(query)}`)
        .then((r) => r.json())
        .then((rows: CanonicalLake[]) => rows),
    enabled: query.trim().length >= 2,
    staleTime: 30_000,
  });

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (value) {
    return (
      <div className="flex min-w-0 items-center justify-between gap-2 rounded-lg border border-border bg-raised px-3 py-2.5">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-primary">
            {value.name}
          </p>
          {value.venue_name && (
            <p className="truncate text-xs text-secondary">
              {value.venue_name}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setQuery("");
          }}
          className="shrink-0 text-secondary hover:text-primary"
          aria-label="Clear lake selection"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  const results: CanonicalLake[] = searchQuery.data ?? [];
  // Show the "Add a lake" affordance whenever a search has been run and returned nothing.
  const showAddLake =
    open &&
    query.trim().length >= 2 &&
    !searchQuery.isLoading &&
    !searchQuery.isError &&
    results.length === 0;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search
          size={15}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search by lake or fishery name…"
          className="w-full rounded-lg border border-border bg-raised py-2.5 pl-9 pr-3 text-sm text-primary placeholder:text-tertiary focus:border-accent focus:outline-none"
          autoComplete="off"
        />
      </div>
      {open && query.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-y-auto rounded-xl border border-border bg-raised shadow-2xl">
          {searchQuery.isLoading && (
            <p className="px-3 py-3 text-sm text-secondary">Searching…</p>
          )}
          {!searchQuery.isLoading && searchQuery.isError && (
            <p className="px-3 py-3 text-sm text-error">
              Lake search unavailable. Please try again.
            </p>
          )}
          {!searchQuery.isLoading &&
            !searchQuery.isError &&
            results.length === 0 && (
              <p className="px-3 py-3 text-sm text-secondary">
                No lakes found in the directory.
              </p>
            )}
          {results.map((lake) => (
            <button
              key={lake.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(lake);
                setOpen(false);
                setQuery("");
              }}
              className="flex w-full min-w-0 flex-col items-start gap-0.5 border-b border-border/60 px-3 py-3 text-left last:border-b-0 hover:bg-inset focus-visible:bg-inset focus-visible:outline-none"
            >
              <span className="truncate text-sm font-medium text-primary">
                {lake.name}
              </span>
              {lake.venue_name && (
                <span className="truncate text-xs text-secondary">
                  {lake.venue_name}
                </span>
              )}
              {lake.county_region && (
                <span className="text-xs text-tertiary">
                  {lake.county_region}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {/* Inline "Add a lake" — shown below the search box when no results, outside the dropdown */}
      {showAddLake && (
        <div className="mt-2 rounded-lg border border-dashed border-border bg-raised px-3 py-3">
          <p className="text-sm text-secondary">
            Can't find your lake in the directory?
          </p>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onAddLake(query);
            }}
            className="mt-2 flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
          >
            <Plus size={14} /> Add a lake
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdvisorPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Restore trip state from URL params when returning from FindLake.
  // FindLake passes ?returnToAdvisor=1 plus encoded trip fields when a lake is added.
  const restoredTrip = useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("returnToAdvisor") !== "1") return null;
    return {
      trip_date: params.get("trip_date") ?? "",
      expected_start_time: params.get("expected_start_time") ?? "05:30",
      expected_duration_hours: params.get("expected_duration_hours") ?? "24",
      target_species: params.get("target_species") ?? "both",
      // The newly added lake's personal-lake id and name are passed back so we
      // can trigger a canonical-lake search and preselect it.
      new_lake_name: params.get("new_lake_name") ?? "",
      new_canonical_id: params.get("new_canonical_id") ?? "",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFutureTrip = true;

  // Fishing Now state
  const [nowValues, setNowValues] = useState({
    lake_id: "",
    fishing_date: todayInput(),
    expected_start_time: currentTimeInput(),
    expected_duration_hours: "24",
    target_species: "both",
    forecast_air_temp_c: "",
    forecast_pressure_hpa: "",
    forecast_pressure_trend: "not-provided",
    forecast_wind_direction: "",
    forecast_wind_speed_mph: "",
    forecast_cloud_pct: "",
    forecast_precipitation_mm: "",
  });

  // Future Trip state — restore from URL params if returning from FindLake.
  const [tripValues, setTripValues] = useState({
    canonical_lake: null as CanonicalLake | null,
    trip_date: restoredTrip?.trip_date ?? "",
    expected_start_time: restoredTrip?.expected_start_time ?? "05:30",
    expected_duration_hours: restoredTrip?.expected_duration_hours ?? "24",
    target_species: restoredTrip?.target_species ?? "both",
  });

  // When returning from FindLake with a new canonical lake, fetch its details
  // and preselect it. Runs once on mount when restoredTrip.new_canonical_id exists.
  useEffect(() => {
    if (!restoredTrip?.new_canonical_id) return;
    // The newly submitted lake may be community_submitted (not active_listed=TRUE yet),
    // so we cannot use the canonical-lakes/search endpoint (which filters active_listed=TRUE).
    // Instead we use the stored name directly to build a CanonicalLake object and
    // preselect it — the advisor POST sends the canonical_lake_id directly.
    const preselected: CanonicalLake = {
      id: restoredTrip.new_canonical_id,
      name: restoredTrip.new_lake_name || "Added lake",
      venue_name: null,
      county_region: null,
      country: null,
    };
    setTripValues((prev) => ({ ...prev, canonical_lake: preselected }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const lakesQuery = useQuery({
    queryKey: ["carpintel_lakes"],
    queryFn: () => fetch("/app-api/lakes").then((r) => r.json()),
  });
  const historyQuery = useQuery({
    queryKey: ["carpintel_advisor_recommendations"],
    queryFn: () =>
      fetch("/app-api/advisor/recommendations").then((r) => r.json()),
  });

  const lakes = lakesQuery.data ?? [];
  const history = historyQuery.data ?? [];

  const updateNow = (key: keyof typeof nowValues, value: string) =>
    setNowValues((c) => ({ ...c, [key]: value }));
  const updateTrip = (key: keyof typeof tripValues, value: any) =>
    setTripValues((c) => ({ ...c, [key]: value }));

  // Navigate to FindLake carrying current trip state so we can restore it on return.
  // FindLake will call back to /advisor?returnToAdvisor=1&new_canonical_id=...&...
  function handleAddLake(searchedName: string) {
    const params = new URLSearchParams();
    params.set("returnToAdvisor", "1");
    if (tripValues.trip_date) params.set("trip_date", tripValues.trip_date);
    params.set("expected_start_time", tripValues.expected_start_time);
    params.set("expected_duration_hours", tripValues.expected_duration_hours);
    params.set("target_species", tripValues.target_species);
    if (searchedName) params.set("prefill_name", searchedName);
    navigate(`/lakes/find?${params.toString()}`);
  }

  function openScout() {
    setMessage("");
    if (
      !nowValues.lake_id ||
      !nowValues.fishing_date ||
      !nowValues.expected_start_time ||
      !nowValues.expected_duration_hours
    ) {
      setMessage("Choose a lake, date, start time and session duration.");
      return;
    }

    const params = new URLSearchParams({
      lake_id: nowValues.lake_id,
      species: nowValues.target_species,
      date: nowValues.fishing_date,
      start: nowValues.expected_start_time,
      duration: nowValues.expected_duration_hours,
    });
    navigate(`/scout?${params.toString()}`);
  }

  async function getAdvice(e: React.FormEvent) {
    e.preventDefault();
    setMessage("");

    if (isFutureTrip) {
      if (
        !tripValues.canonical_lake ||
        !tripValues.trip_date ||
        !tripValues.expected_start_time ||
        !tripValues.expected_duration_hours
      ) {
        setMessage(
          "Choose a lake, trip date, start time and session duration.",
        );
        return;
      }
    } else {
      if (
        !nowValues.lake_id ||
        !nowValues.fishing_date ||
        !nowValues.expected_start_time ||
        !nowValues.expected_duration_hours
      ) {
        setMessage("Choose a lake, date, start time and session duration.");
        return;
      }
    }

    setSaving(true);
    try {
      // Fix 2: For Fish Now, fetch current weather automatically before building
      // the advice request. Failure is silent — advice still generates without it.
      let autoWeather: Record<string, any> = {};
      if (!isFutureTrip && nowValues.lake_id) {
        try {
          const wr = await fetch(
            `/app-api/advisor/current-weather?lake_id=${encodeURIComponent(nowValues.lake_id)}`,
          );
          if (wr.ok) {
            const wdata = await wr.json();
            if (wdata.ok) {
              // Only use auto-weather for fields the user has left blank
              autoWeather = {
                forecast_air_temp_c:
                  nowValues.forecast_air_temp_c || wdata.air_temp_c,
                forecast_pressure_hpa:
                  nowValues.forecast_pressure_hpa || wdata.pressure_hpa,
                forecast_pressure_trend:
                  nowValues.forecast_pressure_trend !== "not-provided"
                    ? nowValues.forecast_pressure_trend
                    : wdata.pressure_trend || "",
                forecast_wind_direction:
                  nowValues.forecast_wind_direction || wdata.wind_direction,
                forecast_wind_speed_mph:
                  nowValues.forecast_wind_speed_mph || wdata.wind_speed_mph,
                forecast_cloud_pct:
                  nowValues.forecast_cloud_pct || wdata.cloud_pct,
                forecast_precipitation_mm:
                  nowValues.forecast_precipitation_mm || wdata.precipitation_mm,
              };
            }
          }
        } catch {
          // Weather fetch failed — continue without it
        }
      }

      const body = isFutureTrip
        ? {
            is_future_trip: true,
            future_trip_canonical_lake_id: tripValues.canonical_lake!.id,
            trip_date: tripValues.trip_date,
            fishing_date: tripValues.trip_date,
            expected_start_time: tripValues.expected_start_time,
            expected_duration_hours: Number(tripValues.expected_duration_hours),
            target_species: tripValues.target_species,
          }
        : {
            is_future_trip: false,
            lake_id: Number(nowValues.lake_id),
            fishing_date: nowValues.fishing_date,
            expected_start_time: nowValues.expected_start_time,
            expected_duration_hours: Number(nowValues.expected_duration_hours),
            target_species: nowValues.target_species,
            forecast_pressure_trend:
              autoWeather.forecast_pressure_trend ??
              (nowValues.forecast_pressure_trend === "not-provided"
                ? ""
                : nowValues.forecast_pressure_trend),
            forecast_air_temp_c:
              autoWeather.forecast_air_temp_c ??
              nowValues.forecast_air_temp_c ??
              null,
            forecast_pressure_hpa:
              autoWeather.forecast_pressure_hpa ??
              nowValues.forecast_pressure_hpa ??
              null,
            forecast_wind_direction:
              autoWeather.forecast_wind_direction ??
              nowValues.forecast_wind_direction ??
              null,
            forecast_wind_speed_mph:
              autoWeather.forecast_wind_speed_mph ??
              nowValues.forecast_wind_speed_mph ??
              null,
            forecast_cloud_pct:
              autoWeather.forecast_cloud_pct ??
              nowValues.forecast_cloud_pct ??
              null,
            forecast_precipitation_mm:
              autoWeather.forecast_precipitation_mm ??
              nowValues.forecast_precipitation_mm ??
              null,
          };

      const selectedTarget = isFutureTrip
        ? tripValues.target_species
        : nowValues.target_species;

      const buildAdvice = async (species: "carp" | "catfish") => {
        const response = await fetch("/app-api/advisor/recommendations", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...body,
            target_species: species,
          }),
        });
        const result = await response.json();
        if (!response.ok || !result.id)
          throw new Error(
            result.error || `Could not create ${species} fishing advice`,
          );
        return result;
      };

      if (selectedTarget === "both") {
        const [carpAdvice, catfishAdvice] = await Promise.all([
          buildAdvice("carp"),
          buildAdvice("catfish"),
        ]);
        navigate(
          `/advisor/both?carp=${encodeURIComponent(String(carpAdvice.id))}&catfish=${encodeURIComponent(String(catfishAdvice.id))}`,
        );
      } else {
        const result = await buildAdvice(selectedTarget as "carp" | "catfish");
        navigate(`/advisor/${result.id}`);
      }
    } catch (err) {
      setMessage(
        err instanceof Error ? err.message : "Could not create advice",
      );
    } finally {
      setSaving(false);
    }
  }

  const showFishingNowEmpty =
    !lakesQuery.isLoading &&
    !lakesQuery.error &&
    !isFutureTrip &&
    lakes.length === 0;
  const showForm =
    isFutureTrip ||
    (!lakesQuery.isLoading && !lakesQuery.error && lakes.length > 0);

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link
          to="/app"
          className="text-sm font-medium text-accent hover:underline"
        >
          My Fishing
        </Link>
        <div className="mt-2 flex min-w-0 items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-inset text-accent">
            <Compass />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-primary">
              Future Advice
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-secondary">
              Plan ahead using your history, lake evidence and the CatchCompass
              knowledge layer.
            </p>
          </div>
        </div>
      </header>

      {lakesQuery.isLoading && !isFutureTrip ? <LoadingBlock /> : null}
      {lakesQuery.error && !isFutureTrip ? <ErrorBlock /> : null}

      {showFishingNowEmpty && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-base font-semibold text-primary">
            Add a lake before asking for advice
          </p>
          <p className="mt-2 text-sm text-secondary">
            Advisor needs a lake to compare your own sessions and anonymous
            lake-level evidence.
          </p>
          <Button asChild className="mt-4">
            <Link to="/lakes">
              Add a lake <ArrowRight />
            </Link>
          </Button>
        </div>
      )}

      {showForm && (
        <form onSubmit={getAdvice} className="space-y-6">
          {isFutureTrip && (
            <p className="text-sm text-secondary">
              Future Trip mode searches all lakes in the directory — no need to
              add a lake to My Lakes first. Evidence is based on seasonal
              patterns, lake history and external reports. Detailed weather
              forecast will become available closer to the trip.
            </p>
          )}

          <section className="space-y-4">
            <SectionHeading>
              Plan the {isFutureTrip ? "trip" : "session"}
            </SectionHeading>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Target species — both modes */}
              <Field label="Target species" id="advisor-species">
                <Select
                  value={
                    isFutureTrip
                      ? tripValues.target_species
                      : nowValues.target_species
                  }
                  onValueChange={(v) =>
                    isFutureTrip
                      ? updateTrip("target_species", v)
                      : updateNow("target_species", v)
                  }
                >
                  <SelectTrigger id="advisor-species">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carp">Carp</SelectItem>
                    <SelectItem value="catfish">Catfish</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {isFutureTrip ? (
                /* Future Trip: canonical lake search */
                <Field label="Lake" id="advisor-future-lake">
                  <CanonicalLakePicker
                    value={tripValues.canonical_lake}
                    onChange={(lake) => updateTrip("canonical_lake", lake)}
                    onAddLake={handleAddLake}
                  />
                </Field>
              ) : (
                /* Fishing Now: personal My Lakes */
                <Field label="Lake" id="advisor-lake">
                  <Select
                    value={nowValues.lake_id}
                    onValueChange={(v) => updateNow("lake_id", v)}
                  >
                    <SelectTrigger id="advisor-lake">
                      <SelectValue placeholder="Choose a lake" />
                    </SelectTrigger>
                    <SelectContent>
                      {lakes.map((lake: any) => (
                        <SelectItem key={lake.id} value={String(lake.id)}>
                          {lake.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              {isFutureTrip ? (
                /* Future Trip: trip date (required) */
                <Field label="Trip date" id="advisor-trip-date">
                  <Input
                    id="advisor-trip-date"
                    type="date"
                    value={tripValues.trip_date}
                    onChange={(e) => updateTrip("trip_date", e.target.value)}
                    required
                  />
                </Field>
              ) : (
                /* Fishing Now: fishing date */
                <Field
                  label="Fishing date"
                  id="advisor-date"
                  help="This date is saved as a calendar day, not shifted by timezone."
                >
                  <Input
                    id="advisor-date"
                    type="date"
                    value={nowValues.fishing_date}
                    onChange={(e) => updateNow("fishing_date", e.target.value)}
                    required
                  />
                </Field>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Expected session start" id="advisor-start-time">
                <Input
                  id="advisor-start-time"
                  type="time"
                  value={
                    isFutureTrip
                      ? tripValues.expected_start_time
                      : nowValues.expected_start_time
                  }
                  onChange={(e) =>
                    isFutureTrip
                      ? updateTrip("expected_start_time", e.target.value)
                      : updateNow("expected_start_time", e.target.value)
                  }
                  required
                />
              </Field>
              <Field
                label="Expected session duration (hours)"
                id="advisor-duration"
                help="Use 24 for a full day or 48 for a weekend session."
              >
                <Input
                  id="advisor-duration"
                  type="number"
                  min="0.5"
                  max="72"
                  step="0.5"
                  value={
                    isFutureTrip
                      ? tripValues.expected_duration_hours
                      : nowValues.expected_duration_hours
                  }
                  onChange={(e) =>
                    isFutureTrip
                      ? updateTrip("expected_duration_hours", e.target.value)
                      : updateNow("expected_duration_hours", e.target.value)
                  }
                  required
                />
              </Field>
            </div>
          </section>

          {/* Forecast conditions — Fishing Now only */}
          {!isFutureTrip && (
            <section className="space-y-4">
              <div>
                <SectionHeading>Forecast conditions</SectionHeading>
                <p className="mt-1 text-sm text-secondary">
                  Optional for now. These fields are ready for automatic weather
                  data later, and blank fields are not treated as facts.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field
                  label="Forecast air temperature (°C)"
                  id="advisor-air-temp"
                >
                  <Input
                    id="advisor-air-temp"
                    type="number"
                    step="0.1"
                    value={nowValues.forecast_air_temp_c}
                    onChange={(e) =>
                      updateNow("forecast_air_temp_c", e.target.value)
                    }
                    placeholder="e.g. 16.5"
                  />
                </Field>
                <Field label="Forecast pressure (hPa)" id="advisor-pressure">
                  <Input
                    id="advisor-pressure"
                    type="number"
                    step="0.1"
                    value={nowValues.forecast_pressure_hpa}
                    onChange={(e) =>
                      updateNow("forecast_pressure_hpa", e.target.value)
                    }
                    placeholder="e.g. 1014"
                  />
                </Field>
                <Field label="Pressure trend" id="advisor-pressure-trend">
                  <Select
                    value={nowValues.forecast_pressure_trend}
                    onValueChange={(v) =>
                      updateNow("forecast_pressure_trend", v)
                    }
                  >
                    <SelectTrigger id="advisor-pressure-trend">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRESSURE_TRENDS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field
                  label="Forecast wind direction"
                  id="advisor-wind-direction"
                >
                  <Input
                    id="advisor-wind-direction"
                    value={nowValues.forecast_wind_direction}
                    onChange={(e) =>
                      updateNow("forecast_wind_direction", e.target.value)
                    }
                    placeholder="e.g. SW"
                  />
                </Field>
                <Field
                  label="Forecast wind speed (mph)"
                  id="advisor-wind-speed"
                >
                  <Input
                    id="advisor-wind-speed"
                    type="number"
                    step="0.1"
                    min="0"
                    value={nowValues.forecast_wind_speed_mph}
                    onChange={(e) =>
                      updateNow("forecast_wind_speed_mph", e.target.value)
                    }
                    placeholder="e.g. 12"
                  />
                </Field>
                <Field label="Forecast cloud cover (%)" id="advisor-cloud">
                  <Input
                    id="advisor-cloud"
                    type="number"
                    min="0"
                    max="100"
                    value={nowValues.forecast_cloud_pct}
                    onChange={(e) =>
                      updateNow("forecast_cloud_pct", e.target.value)
                    }
                    placeholder="e.g. 70"
                  />
                </Field>
                <Field
                  label="Forecast precipitation (mm)"
                  id="advisor-rain"
                  help="Used as the rain signal when automatic weather is connected."
                >
                  <Input
                    id="advisor-rain"
                    type="number"
                    min="0"
                    step="0.1"
                    value={nowValues.forecast_precipitation_mm}
                    onChange={(e) =>
                      updateNow("forecast_precipitation_mm", e.target.value)
                    }
                    placeholder="e.g. 2.5"
                  />
                </Field>
              </div>
            </section>
          )}

          {message ? (
            <p
              className="rounded-md bg-error-weak p-3 text-sm text-error"
              role="alert"
            >
              {message}
            </p>
          ) : null}
          <div className="sticky bottom-3 flex flex-col gap-3 rounded-lg border border-border bg-raised p-3 sm:flex-row">
            <Button type="submit" size="xl" isLoading={saving}>
              <Compass /> Get Fishing Advice
            </Button>
            {!isFutureTrip ? (
              <Button
                type="button"
                variant="secondary"
                size="xl"
                onClick={openScout}
                disabled={saving}
              >
                <MapPinned /> Scout &amp; Choose
              </Button>
            ) : null}
            <Button asChild variant="tertiary" size="xl">
              <Link to="/app">Cancel</Link>
            </Button>
          </div>
        </form>
      )}

      {/* Previous advice history */}
      {!historyQuery.isLoading && history.length > 0 ? (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <History className="text-secondary" />
            <h2 className="text-lg font-semibold text-primary">
              Previous advice
            </h2>
          </div>
          <div className="space-y-2">
            {history.map((item: any) => (
              <Link
                key={item.id}
                to={`/advisor/${item.id}`}
                className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-border bg-raised p-3 transition-colors hover:bg-inset"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-primary">
                    {item.future_trip_lake_name || item.lake_name} ·{" "}
                    {formatAdvisorDate(item.trip_date || item.fishing_date)}
                  </p>
                  <p className="truncate text-xs text-secondary">
                    {item.is_future_trip ? "Future trip · " : ""}
                    {item.confidence_level} · {item.evidence_session_count}{" "}
                    comparable{" "}
                    {Number(item.evidence_session_count) === 1
                      ? "session"
                      : "sessions"}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-semibold text-primary">
                  {item.fishing_score}/100
                </span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="rounded-lg border border-border bg-raised p-6 text-center text-sm text-secondary">
      Loading your lakes…
    </div>
  );
}

function ErrorBlock() {
  return (
    <div className="rounded-md border border-error bg-error-weak p-4 text-sm text-error">
      Could not load your lakes. Refresh and try again.
    </div>
  );
}
